import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { submitSong } from '../../services/firebase/games';
import { proposeChatterSong, submitChatterSongVote, aggregateSongVotes } from '../../services/firebase/chatters';
import { logError } from '../../utils/logger';
import { isProfane } from '../../utils/profanity';

// Shown when round.chooserId === CHATTERS_ID.
// Chatters see a poll/voting UI. Host sees the poll + confirm/override buttons. Others see it read-only.
export default function ChatterSongSelectionPhase({
  game,
  round,
  isChatter,
  isHost,
  chatterVotes,
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const myVote = chatterVotes[user?.uid];
  const proposals = aggregateSongVotes(chatterVotes);
  const topProposal = proposals[0] ?? null;

  const [submitting, setSubmitting] = useState(false);
  const [showHostOverride, setShowHostOverride] = useState(false);

  async function handleConfirmSong(song) {
    setSubmitting(true);
    try {
      await submitSong(game.id, round.id, song);
    } catch (err) {
      logError(err);
      setSubmitting(false);
    }
  }

  const chatterCount = Object.keys(chatterVotes).length;
  const votedCount = proposals.reduce((sum, p) => sum + p.voters.length, 0);

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-4 py-8">
      <div className="w-full max-w-lg space-y-5">
        {chatterCount > 0 && (
          <p className="text-sm t-text-muted text-center">
            {t('chatterSongSelection.chattersVoted', { voted: votedCount, total: chatterCount })}
          </p>
        )}

        {/* Chatter: propose (once only) or show locked proposal */}
        {isChatter && (
          myVote?.proposedSong ? (
            <LockedProposal song={myVote.proposedSong} />
          ) : (
            <ProposalForm
              gameId={game.id}
              roundId={round.id}
              userId={user.uid}
            />
          )
        )}

        {/* Live poll — shown to everyone */}
        {proposals.length > 0 && (
          <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-3">
            <h3 className="text-sm font-semibold t-text-secondary">
              {isChatter ? t('chatterSongSelection.currentVotes') : t('chatterSongSelection.chatVote')}
            </h3>
            <ul className="space-y-2">
              {proposals.map((proposal, idx) => {
                const myProposedKey = myVote?.proposedSong
                  ? (myVote.proposedSong.videoId || `${myVote.proposedSong.title}||${myVote.proposedSong.artist}`)
                  : null;
                const myVoteKey = myVote?.songVote
                  ? (myVote.songVote.videoId || `${myVote.songVote.title}||${myVote.songVote.artist}`)
                  : null;
                const isMyProposal = myProposedKey === proposal.key;
                const isMyCurrentVote = myVoteKey === proposal.key;
                return (
                  <li key={proposal.key}>
                    <div
                      className={`flex items-center gap-3 rounded-xl p-3 transition-colors ${
                        isMyCurrentVote
                          ? 't-bg-accent-muted t-border-accent border'
                          : 't-bg-input'
                      }`}
                    >
                      {/* Vote count indicator */}
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-lg font-bold t-text-muted w-5 text-center">
                          {idx + 1}
                        </span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: proposal.voters.length }).map((_, i) => (
                            <span key={i} className="w-2 h-2 rounded-full bg-(--c-text-accent)" aria-hidden="true" />
                          ))}
                        </div>
                        <span className="text-xs t-text-muted font-medium">
                          {proposal.voters.length}
                        </span>
                      </div>

                      <div className="flex-1 min-w-0">
                        <p className="t-text text-sm font-medium leading-tight">
                          {proposal.song.title}
                        </p>
                        <p className="t-text-muted text-xs">{proposal.song.artist}</p>
                      </div>

                      {/* Chatter: switch vote to this proposal */}
                      {isChatter && !isMyCurrentVote && (
                        <button
                          type="button"
                          aria-label={t('chatterSongSelection.voteFor', { title: proposal.song.title })}
                          onClick={() =>
                            submitChatterSongVote(game.id, round.id, user.uid, proposal.song)
                          }
                          className="shrink-0 text-xs t-btn-primary px-2.5 py-1.5 rounded-lg transition-colors t-focus-ring"
                        >
                          {t('chatterSongSelection.vote')}
                        </button>
                      )}
                      <div className="shrink-0 flex flex-col items-end gap-0.5">
                        {isMyCurrentVote && (
                          <span className="text-xs t-text-accent font-medium">{t('chatterSongSelection.yourVote')}</span>
                        )}
                        {isMyProposal && (
                          <span className="text-xs t-text-secondary font-medium">{t('chatterSongSelection.yourProposal')}</span>
                        )}
                      </div>

                      {/* Host: use this song */}
                      {isHost && !showHostOverride && (
                        <button
                          type="button"
                          aria-label={t('chatterSongSelection.confirmSong', { title: proposal.song.title })}
                          onClick={() => handleConfirmSong(proposal.song)}
                          disabled={submitting}
                          className="shrink-0 text-xs t-btn-success disabled:opacity-50 px-2.5 py-1.5 rounded-lg transition-colors t-focus-ring"
                        >
                          {submitting ? '…' : t('chatterSongSelection.use')}
                        </button>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>

            {/* Host: confirm top vote */}
            {isHost && !showHostOverride && topProposal && (
              <button
                type="button"
                onClick={() => handleConfirmSong(topProposal.song)}
                disabled={submitting}
                className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-4 py-3 transition-colors mt-2 t-focus-ring"
              >
                {submitting ? t('chatterSongSelection.starting') : t('chatterSongSelection.useTopVote', { title: topProposal.song.title })}
              </button>
            )}
          </div>
        )}

        {proposals.length === 0 && !isChatter && !isHost && (
          <div className="flex items-center justify-center gap-3">
            <div
              className="w-5 h-5 border-2 t-border-accent border-t-transparent rounded-full animate-spin shrink-0"
              aria-hidden="true"
            />
            <p className="t-text-muted text-sm">{t('chatterSongSelection.waitingForProposals')}</p>
          </div>
        )}

        {/* Host override — lets host close chatter voting and pick the song themselves */}
        {isHost && (
          <div className="t-border border-t pt-4">
            {!showHostOverride ? (
              <button
                type="button"
                onClick={() => setShowHostOverride(true)}
                className="w-full text-sm t-text-disabled hover:t-text-muted transition-colors py-2 t-focus-ring rounded"
              >
                {proposals.length === 0
                  ? t('chatterSongSelection.noProposalsYet')
                  : t('chatterSongSelection.overrideOption')}
              </button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-semibold t-text-secondary">{t('chatterSongSelection.hostOverride')}</p>
                  <button
                    type="button"
                    onClick={() => setShowHostOverride(false)}
                    className="text-xs t-text-disabled hover:t-text-muted transition-colors t-focus-ring rounded"
                  >
                    {t('chatterSongSelection.cancel')}
                  </button>
                </div>
                <HostOverrideForm
                  onSubmit={handleConfirmSong}
                  submitting={submitting}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// Proposal form for chatters — only shown before they have made their first proposal.
function ProposalForm({ gameId, roundId, userId }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');
  const [proposeError, setProposeError] = useState('');
  const [proposing, setProposing] = useState(false);

  async function handleManualPropose(e) {
    e.preventDefault();
    if (!title.trim()) return;
    setProposeError('');
    if (isProfane(title) || isProfane(artist)) {
      setProposeError(t('chatterSongSelection.profanityError'));
      return;
    }
    setProposing(true);
    try {
      await proposeChatterSong(gameId, roundId, userId, {
        title: title.trim(),
        artist: artist.trim(),
      });
    } catch (err) {
      logError(err);
    } finally {
      setProposing(false);
    }
  }

  return (
    <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-3">
      <p className="text-sm t-text-muted">{t('chatterSongSelection.proposeSong')}</p>
      <form onSubmit={handleManualPropose} className="space-y-2">
        <label htmlFor="chatter-proposal-title" className="sr-only">{t('chatterSongSelection.songTitleLabel')}</label>
        <input
          id="chatter-proposal-title"
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder={t('chatterSongSelection.songTitlePlaceholder')}
          maxLength={200}
          className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2.5 t-text t-placeholder t-focus-input text-sm"
        />
        <label htmlFor="chatter-proposal-artist" className="sr-only">{t('chatterSongSelection.artistLabel')}</label>
        <input
          id="chatter-proposal-artist"
          type="text"
          value={artist}
          onChange={(e) => setArtist(e.target.value)}
          placeholder={t('chatterSongSelection.artistPlaceholder')}
          maxLength={80}
          className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2.5 t-text t-placeholder t-focus-input text-sm"
        />
        {proposeError && (
          <p role="alert" className="t-text-error text-xs">{proposeError}</p>
        )}
        <button
          type="submit"
          disabled={proposing || !title.trim()}
          className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-3 py-2.5 text-sm transition-colors t-focus-ring"
        >
          {proposing ? t('chatterSongSelection.proposing') : t('chatterSongSelection.proposeSongButton')}
        </button>
      </form>
    </div>
  );
}

// Shown after a chatter has submitted their proposal — the proposal is now locked.
function LockedProposal({ song }) {
  const { t } = useTranslation();
  return (
    <div className="t-bg-surface t-border border rounded-2xl p-5 space-y-1">
      <p className="text-xs font-semibold t-text-secondary uppercase tracking-widest">
        {t('chatterSongSelection.yourProposal')}
      </p>
      <p className="t-text text-sm font-medium">{song.title}</p>
      {song.artist && <p className="t-text-muted text-xs">{song.artist}</p>}
      <p className="text-xs t-text-muted pt-1">{t('chatterSongSelection.alreadyProposedHint')}</p>
    </div>
  );
}

// Host-only form to pick a song and bypass the chatter poll
function HostOverrideForm({ onSubmit, submitting }) {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [artist, setArtist] = useState('');

  return (
    <div className="space-y-2">
      <label htmlFor="host-override-title" className="sr-only">{t('chatterSongSelection.songTitleLabel')}</label>
      <input
        id="host-override-title"
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder={t('chatterSongSelection.songTitlePlaceholder')}
        maxLength={200}
        className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2.5 t-text t-placeholder t-focus-input text-sm"
      />
      <label htmlFor="host-override-artist" className="sr-only">{t('chatterSongSelection.artistLabel')}</label>
      <input
        id="host-override-artist"
        type="text"
        value={artist}
        onChange={(e) => setArtist(e.target.value)}
        placeholder={t('chatterSongSelection.artistPlaceholder')}
        maxLength={80}
        className="w-full t-bg-input t-border-input border rounded-lg px-3 py-2.5 t-text t-placeholder t-focus-input text-sm"
      />
      <button
        type="button"
        onClick={() => onSubmit({ title: title.trim(), artist: artist.trim() })}
        disabled={submitting || !title.trim()}
        className="w-full t-btn-primary disabled:opacity-50 disabled:cursor-not-allowed font-semibold rounded-lg px-3 py-2.5 text-sm transition-colors t-focus-ring"
      >
        {submitting ? t('chatterSongSelection.starting') : t('chatterSongSelection.useThisSong')}
      </button>
    </div>
  );
}
